import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react"

import axios from "axios"

import { Subscription } from "@rails/actioncable"

import EventEmitter from "src/EventEmitter"
import dayjs from "src/dayjs"

import { gql, useClient, useQuery } from "hooks/urql"
import useWebsocket from "hooks/useWebsocket"

import CaptionView from "components/AudioRoom/CaptionView"
import MuteButton from "components/AudioRoom/MuteButton"
import { ChimeJoinInfo, HandleAudioRoomError } from "components/AudioRoom/types"
import useAudioRoom from "components/AudioRoom/useAudioRoom"
import useClassPeriodRefresh from "components/AudioRoom/useClassPeriodRefresh"
import useGeminiLiveTranslation from "components/ChimeMeetingHooks/useGeminiLiveTranslation"
import usePlaybackRate from "components/ChimeMeetingHooks/usePlaybackRate"
import useTranscriptForwarding from "components/ChimeMeetingHooks/useTranscriptForwarding"
import NotificationContainer from "components/NotificationContainer"
import {
  AcrClassPrompt,
  PromptClosedEventPayload,
  PromptOpenedEventPayload,
} from "components/Pages/Teacher/AudioRooms/TeacherPrompts/types"

import StudentPromptCard from "./StudentPromptCard"
import StudentVocabularyPanel from "./StudentVocabularyPanel"
import useStudentControlsSubscriptions from "./useStudentControlsSubscriptions"
import useVocabularyWords from "./useVocabularyWords"

type StudentRoomProps = {
  joinInfo: ChimeJoinInfo
  handleError: HandleAudioRoomError
  setUserError: Dispatch<SetStateAction<string | undefined>>
  audioRoomId: string
  currentUserId: string
  audioInputDeviceId: string
  audioInputDeviceName: string
}

const StudentRoom: React.FC<StudentRoomProps> = ({
  joinInfo,
  handleError,
  setUserError,
  audioRoomId,
  currentUserId,
  audioInputDeviceId,
  audioInputDeviceName,
}) => {
  const { connected, muted, mute, unmute, roster, audioVideo } = useAudioRoom(
    joinInfo,
    handleError,
    setUserError,
    audioRoomId,
    currentUserId,
    audioInputDeviceId
  )
  const [activePrompt, setActivePrompt] = useState<AcrClassPrompt>()

  useEffect(() => {
    const onPromptOpened = ({
      promptId,
      body,
      answerType,
    }: PromptOpenedEventPayload) =>
      setActivePrompt({ id: promptId, body, answerType })
    const onPromptClosed = ({ promptId }: PromptClosedEventPayload) => {
      setActivePrompt(current =>
        current?.id === promptId ? undefined : current
      )
    }

    EventEmitter.subscribe("acr_class_prompt_opened", onPromptOpened)
    EventEmitter.subscribe("acr_class_prompt_closed", onPromptClosed)
    return () => {
      EventEmitter.unsubscribe("acr_class_prompt_opened", onPromptOpened)
      EventEmitter.unsubscribe("acr_class_prompt_closed", onPromptClosed)
    }
  }, [])

  const channelProps = useMemo(() => ({ id: audioRoomId }), [audioRoomId])
  const { channel }: { channel: Subscription } = useWebsocket({
    channelName: "AudioRoomChannel",
    channelProps,
  })
  useWebsocket({
    channelName: "AudioRoomUserChannel",
    channelProps,
  })
  const { localControlsLocked } = useStudentControlsSubscriptions({
    localUserId: currentUserId,
    mute,
    unmute,
  })
  useEffect(() => {
    mute()
  }, [mute])

  const client = useClient()
  const [classPeriodsResult, refetchClassPeriods] = useQuery({
    query: classPeriodsQuery,
    client,
    variables: { audioRoomId },
  })
  const activeClassPeriod =
    classPeriodsResult.data?.audioRoom?.activeAcrClassPeriod
  const { words: vocabularyWords, latestWordId } = useVocabularyWords({
    classPeriodId: activeClassPeriod?.id,
    initialWords: activeClassPeriod?.vocabularyWords,
  })

  useClassPeriodRefresh(
    activeClassPeriod?.endsAt,
    classPeriodsResult.data?.audioRoom?.upcomingAcrClassPeriod?.startsAt,
    refetchClassPeriods
  )

  const classPeriodHeading = activeClassPeriod
    ? `${activeClassPeriod.acrClass.name}, ${dayjs(
        activeClassPeriod.startsAt
      ).format("h:mm A")}–${dayjs(activeClassPeriod.endsAt).format("h:mm A")}`
    : "Audio Classroom"

  const teacherAttendeeId = Object.values(roster).find(
    entry => entry.userType === "Teacher"
  )?.attendeeId
  const localRosterEntry = roster[joinInfo.attendee.attendeeId]

  usePlaybackRate({
    audioVideo,
    playbackRate: localRosterEntry?.defaultAudioPlaybackRate ?? 1,
    instructorAttendeeId: teacherAttendeeId,
  })

  useGeminiLiveTranslation({
    audioVideo,
    targetLanguageCode: localRosterEntry?.defaultLiveTranslationLanguageCode,
    chimeMeetingId: joinInfo.meeting.meeting_id,
  })

  useTranscriptForwarding({
    enabled: !localRosterEntry?.defaultLiveTranslationLanguageCode, // disable if translating
    audioVideo,
    userId: String(currentUserId),
    channel,
  })

  useEffect(() => {
    const onRemoveStudent = ({
      redirectUrl,
      message,
    }: {
      redirectUrl: string
      message: string
    }) => {
      audioVideo?.stop()
      axios.post("/api/set_flash", { type: "alert", message }).finally(() => {
        window.location.href = redirectUrl
      })
    }

    EventEmitter.subscribe("remove_student", onRemoveStudent)
    return () => EventEmitter.unsubscribe("remove_student", onRemoveStudent)
  }, [audioVideo])

  return (
    <>
      <NotificationContainer />
      <audio className="hidden" id="room-audio" />
      <div className="flex h-screen flex-col">
        <header className="flex-none border-b-4 border-solid border-neutral-700 p-6">
          <h1 className="text-xl font-semibold">{classPeriodHeading}</h1>
        </header>
        <main className="flex min-h-0 flex-1 flex-col">
          {activePrompt && (
            <StudentPromptCard
              prompt={activePrompt}
              onSubmitted={() => setActivePrompt(undefined)}
            />
          )}
          <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)_minmax(12rem,40%)] md:grid-cols-[minmax(0,1fr)_20rem] md:grid-rows-1">
            <section className="relative min-h-0">
              <CaptionView
                audioVideo={audioVideo}
                roster={roster}
                highlightedWords={vocabularyWords.flatMap(word => [
                  word.word,
                  ...word.spokenForms,
                ])}
              />
              <MuteButton
                connected={connected}
                muted={muted}
                toggleMute={() => (muted ? unmute : mute)()}
                controlsLocked={localControlsLocked}
                microphoneName={audioInputDeviceName}
              />
            </section>
            <div className="min-h-0 border-t border-l-0 border-r-0 border-b-0 border-solid border-neutral-700 md:border-l md:border-t-0">
              <StudentVocabularyPanel
                classPeriodId={activeClassPeriod?.id}
                latestWordId={latestWordId}
                translationLanguageCode={
                  localRosterEntry?.defaultLiveTranslationLanguageCode
                }
                words={vocabularyWords}
              />
            </div>
          </div>
        </main>
      </div>
    </>
  )
}

const classPeriodsQuery = gql`
  query StudentAudioRoomClassPeriods($audioRoomId: ID!) {
    audioRoom(id: $audioRoomId) {
      activeAcrClassPeriod {
        id
        startsAt
        endsAt
        vocabularyWords {
          id
          word
          spokenForms
          partOfSpeech
          definition
          translations
          occurrenceCount
          lastSpokenAt
        }
        acrClass {
          name
        }
      }
      upcomingAcrClassPeriod {
        startsAt
      }
    }
  }
`

export default StudentRoom
